import { z } from 'zod';

/** Raster formats only: SVG can carry script, so it is never accepted. */
export const logoContentTypes = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type LogoContentType = (typeof logoContentTypes)[number];

/** Largest logo the club may store, after decoding. */
export const maximumLogoBytes = 256 * 1024;

/** The logo travels as base64 inside JSON, which is a third larger than the image itself. */
const maximumEncodedLength = Math.ceil(maximumLogoBytes / 3) * 4;

export const uploadLogoRequestSchema = z.strictObject({
  contentType: z.enum(logoContentTypes),
  dataBase64: z
    .string()
    .min(4)
    .max(maximumEncodedLength)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/),
});
export type UploadLogoRequest = z.infer<typeof uploadLogoRequestSchema>;

/** The format the first bytes announce, or null when they match none of the allowed formats. */
export function detectLogoContentType(bytes: Uint8Array): LogoContentType | null {
  const startsWith = (signature: readonly number[], offset = 0): boolean =>
    signature.every((value, index) => bytes[offset + index] === value);
  if (startsWith([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith([0xff, 0xd8, 0xff])) return 'image/jpeg';
  // WebP is a RIFF container: "RIFF", four size bytes, then "WEBP".
  if (startsWith([0x52, 0x49, 0x46, 0x46]) && startsWith([0x57, 0x45, 0x42, 0x50], 8)) {
    return 'image/webp';
  }
  return null;
}
